import React, { useEffect, useRef, useState } from "react";
import { Search, Plus, MoreHorizontal } from "lucide-react";
import { MdOutlineGroupAdd } from "react-icons/md";
import NewContractor from "./NewContractor";
import WorkOrder from "./WorkOrder";
import ContractorDetails from "./ContractorDetails";
import profileIcon from "../../../assets/contractor_icon.png";
import { json } from "utils/http/client";
import { useNavigate } from "react-router-dom";
import AddWorkOrder from "./AddWorkOrder";
import OrganizationEmptyState from "components/UI_components/OrganizationEmptyState";
import NoDataIcon from "assets/empty state/NoItems.svg";
import RequireAccess from "components/RequireAccess";
import Loader from "components/UI_components/Loader";
export default function Contractors({ currentMember }) {
  const [contractors, setContractors] = useState([]);

  const [searchTerm, setSearchTerm] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");
  const [sortField, setSortField] = useState("name");
  const [showSortMenu, setShowSortMenu] = useState(false);
  const [showAddContractor, setShowAddContractor] = useState(false);
  const [selectedContractor, setSelectedContractor] = useState(null);
  const [showWorkOrderPopup, setShowWorkOrderPopup] = useState(false);
  const [showNewWorkOrder, setShowNewWorkOrder] = useState(null);
  const [refetch, setRefetch] = useState(0);
  const [contractorLoading, setContractorLoading] = useState(false);
  const organizationId = JSON.parse(localStorage.getItem("organization"))?.id;
  const navigate = useNavigate();
  const sortMenuRef = useRef(null);

  useEffect(() => {
    const orgId = JSON.parse(localStorage.getItem("organization"))?.id;
    if (!orgId) return;

    const fetchContractors = async () => {
      setContractorLoading(true);
      try {
        const res = await json("/org/contractors/", {
          method: "POST",
          body: { organization_id: Number(orgId) },
        });
        const list = Array.isArray(res?.data)
          ? res.data
          : Array.isArray(res?.results)
            ? res.results
            : Array.isArray(res)
              ? res
              : [];
        setContractors(list || []);
      } catch (e) {
        console.error("Failed to fetch contractors", e);
        setContractors([]);
      }
      finally {
        setContractorLoading(false);
      }
    };

    fetchContractors();
  }, [refetch]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (sortMenuRef.current && !sortMenuRef.current.contains(event.target)) {
        setShowSortMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredContractors = [...contractors]
    .filter((contractor) =>
      contractor.name?.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      if (sortField === "name") {
        const nameA = a.name || "";
        const nameB = b.name || "";
        return sortOrder === "asc"
          ? nameA.localeCompare(nameB)
          : nameB.localeCompare(nameA);
      }
      const getNumber = (val) => {
        const num = Number(val);
        return Number.isFinite(num) ? num : 0;
      };
      const aVal =
        sortField === "advance_payment"
          ? getNumber(a.advance_payment)
          : getNumber(a.total_paid);
      const bVal =
        sortField === "advance_payment"
          ? getNumber(b.advance_payment)
          : getNumber(b.total_paid);
      return sortOrder === "asc" ? aVal - bVal : bVal - aVal;
    });

  const handleOpenWorkOrder = (contractor) => {
    setShowWorkOrderPopup(false);
    setShowNewWorkOrder(contractor);
  };

  if (!organizationId) {
    return <OrganizationEmptyState />;
  }

  if (currentMember?.contractor_control === false && currentMember?.joined_as !== "owner" && currentMember?.is_admin === false) {
    return (
      <RequireAccess
        module="Contractor"
        currentMember={currentMember}
        permission="contractor_control"
      />
    );
  }

  if (showNewWorkOrder) {
    return (
      <AddWorkOrder
        contractor={showNewWorkOrder}
        onClose={() => setShowNewWorkOrder(null)}
      />
    );
  }

  return (
    <div className=" text-black flex flex-col mx-auto w-[82%] h-[92vh] overflow-y-scroll pt-4">
      {selectedContractor ? (
        <ContractorDetails
          contractor={selectedContractor}
          onBack={() => setSelectedContractor(null)}
        />
      ) : (
        <>
          {/* Top Bar */}
          <div
            className="flex items-center pr-6 justify-between"
            style={{ height: "48px", gap: "20px" }}
          >
            <h2 className="text-[16px] ml-4 text-[#737373] whitespace-nowrap">
              {`${filteredContractors.length} Contractors`}
            </h2>

            <div className="flex items-center gap-[20px]">
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-[#877A90]" />
                <input
                  type="text"
                  placeholder="Search in contractor"
                  className="w-[259.51px] h-[34px] pl-8 pr-3 py-1.5 rounded-[6px] border border-[#CCCCCC] bg-[#FFFFFF] text-[15px] text-[#877A90] placeholder-[#877A90] focus:outline-none focus:ring-2 focus:ring-[#3B0069]"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>

              <div className="relative" ref={sortMenuRef}>
                <button
                  onClick={() => setShowSortMenu((prev) => !prev)}
                  className="flex items-center justify-center gap-1 bg-[#ECE6F0] text-[#3B0069] text-sm font-medium rounded-md"
                  style={{ width: "120px", height: "32px", padding: "4px 12px" }}
                >
                  Sort ↓
                </button>
                {showSortMenu && (
                  <div
                    className="absolute right-0 mt-1 w-44 bg-white border border-gray-200 rounded-md shadow-lg z-10"
                    role="menu"
                    aria-label="Sort options"
                    style={{ minWidth: "260px" }}
                  >
                    <button
                      className={`w-full text-left px-3 py-1.5 text-sm hover:bg-gray-100 ${
                        sortField === "name" && sortOrder === "asc"
                          ? "font-semibold text-[#3B0069]"
                          : ""
                      }`}
                      onClick={() => {
                        setSortField("name");
                        setSortOrder("asc");
                        setShowSortMenu(false);
                      }}
                    >
                      Name (A → Z)
                    </button>
                    <button
                      className={`w-full text-left px-3 py-1.5 text-sm hover:bg-gray-100 ${
                        sortField === "name" && sortOrder === "desc"
                          ? "font-semibold text-[#3B0069]"
                          : ""
                      }`}
                      onClick={() => {
                        setSortField("name");
                        setSortOrder("desc");
                        setShowSortMenu(false);
                      }}
                    >
                      Name (Z → A)
                    </button>
                    <button
                      className={`w-full text-left px-3 py-1.5 text-sm hover:bg-gray-100 ${
                        sortField === "advance_payment" && sortOrder === "desc"
                          ? "font-semibold text-[#3B0069]"
                          : ""
                      }`}
                      onClick={() => {
                        setSortField("advance_payment");
                        setSortOrder("desc");
                        setShowSortMenu(false);
                      }}
                    >
                      Advance Amount (High → Low)
                    </button>
                    <button
                      className={`w-full text-left px-3 py-1.5 text-sm hover:bg-gray-100 ${
                        sortField === "advance_payment" && sortOrder === "asc"
                          ? "font-semibold text-[#3B0069]"
                          : ""
                      }`}
                      onClick={() => {
                        setSortField("advance_payment");
                        setSortOrder("asc");
                        setShowSortMenu(false);
                      }}
                    >
                      Advance Amount (Low → High)
                    </button>
                    <button
                      className={`w-full text-left px-3 py-1.5 text-sm hover:bg-gray-100 ${
                        sortField === "total_paid" && sortOrder === "desc"
                          ? "font-semibold text-[#3B0069]"
                          : ""
                      }`}
                      onClick={() => {
                        setSortField("total_paid");
                        setSortOrder("desc");
                        setShowSortMenu(false);
                      }}
                    >
                      Total Paid (High → Low)
                    </button>
                    <button
                      className={`w-full text-left px-3 py-1.5 text-sm hover:bg-gray-100 ${
                        sortField === "total_paid" && sortOrder === "asc"
                          ? "font-semibold text-[#3B0069]"
                          : ""
                      }`}
                      onClick={() => {
                        setSortField("total_paid");
                        setSortOrder("asc");
                        setShowSortMenu(false);
                      }}
                    >
                      Total Paid (Low → High)
                    </button>
                  </div>
                )}
              </div>

              <button
                onClick={() => setShowWorkOrderPopup(true)}
                className="flex items-center space-x-2 bg-[#FFFFFF] text-[#3B0069] border-1 border-[#3B0069] text-[14px] rounded-[10px] px-4 py-1.5"
                style={{ height: "38px", width: "166px" }}
              >
                <div className="w-6 h-6 rounded-full flex items-center justify-center">
                  <Plus className="w-4 h-4 text-[#3B0069]" />
                </div>
                <span>Work Order</span>
              </button>

              <button
                onClick={() => setShowAddContractor(true)}
                className="flex items-center space-x-2 bg-[#3B0069] text-white text-[14px] rounded-[10px] px-4 py-1.5 hover:bg-[#2a004c] transition"
                style={{ height: "38px" }}
              >
                <div className="w-6 h-6 rounded-full bg-[#5E2D91] flex items-center justify-center">
                  <MdOutlineGroupAdd />
                </div>
                <span>Add Contractor</span>
              </button>
            </div>
          </div>

          {/* Contractor Cards */}
          <div
            className="space-y-2 overflow-y-auto pr-1 mt-3"
            style={{
              maxHeight: filteredContractors.length > 7 ? "510px" : "auto",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {
              contractorLoading ? (
                <Loader />

              ) :
                filteredContractors.length > 0 ? (
                  filteredContractors.map((contractor, index) => (
                    <div
                      key={index}
                      className="flex flex-row px-4 py-2 mx-2 items-center text-black cursor-pointer"
                      style={{
                        borderRadius: "4px",
                        border: "1px solid #8686864D",
                        background: "#FFFFFF",
                        transition: "transform 0.2s",
                      }}
                      onClick={() =>
                        navigate(`/dashboard/contractor/${contractor.id}`)
                      }
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = "scale(1.01)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "scale(1)";
                      }}
                    >
                      <div className="flex items-center justify-between w-full gap-10">
                        {/* Left - Contractor Info */}
                        <div
                          className="flex items-center gap-3 min-w-0 flex-shrink-0"
                          style={{ flex: "0 0 20%" }}
                        >
                          <div className="w-10 h-10 flex-shrink-0">
                            <img
                              src={profileIcon}
                              alt="Contractor Icon"
                              className="w-full h-full object-contain"
                            />
                          </div>
                          <div className="min-w-0">
                            <p className="text-base text-[#000000] cursor-pointer truncate">
                              {contractor.name}
                            </p>
                            <p className="text-xs text-[#000000] truncate">
                              {contractor.contractor_type}
                            </p>
                          </div>
                        </div>

                        {/* Middle - Stats Grid */}
                        <div className="flex items-center gap-8 flex-1">
                          <div className="text-left space-y-1 flex-1 min-w-0">
                            <p className="text-xs text-[#737373]">Total Paid</p>
                            <p className="text-base text-[#232323] truncate">
                              ₹ {contractor.total_paid?.toLocaleString() || "0"}
                            </p>
                          </div>

                          <div className="text-left space-y-1 flex-1 min-w-0">
                            <p className="text-xs text-[#737373]">Total Due</p>
                            <p className="text-base text-[#232323] truncate">
                              ₹ {contractor.due_payment?.toLocaleString() || "0"}
                            </p>
                          </div>

                          <div className="text-left space-y-1 flex-1 min-w-0">
                            <p className="text-xs text-[#737373]">
                              Projects Associated
                            </p>
                            <p className="px-2 py-1 text-xs font-semibold inline-flex justify-center items-center text-[#3B0069] border rounded-full bg-[#ECE6F0] whitespace-nowrap">
                              {contractor.projects_associated?.toLocaleString() ||
                                "0"}
                            </p>
                          </div>
                        </div>

                        {/* Right - More Icon */}
                        {/* <div className="flex-shrink-0">
                          <MoreHorizontal className="text-[#232323] cursor-pointer" />
                        </div> */}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="flex flex-col items-center justify-center py-14">
                    <img
                      src={NoDataIcon}
                      alt="No Contractors"
                      className="mb-4"
                      width={240}
                    />
                    <span className="text-gray-500 text-2xl">
                      No Contractors found
                    </span>
                    <span className="text-gray-500 text-sm">
                      Add a new contractor to get started.
                    </span>
                  </div>
                )}
          </div>
        </>
      )}

      {showAddContractor && (
        <NewContractor
          onClose={() => {
            setShowAddContractor(false);
            setRefetch((prev) => prev + 1);
          }}
          onSave={() => {
            setShowAddContractor(false);
            setRefetch((prev) => prev + 1);
          }}
        />
      )}

      {showWorkOrderPopup && (
        <WorkOrder
          contractors={contractors}
          handleOpenWorkOrder={handleOpenWorkOrder}
          onClose={() => setShowWorkOrderPopup(false)}
        />
      )}
    </div>
  );
}